import { useRef, useEffect } from 'react';
import { Loader2, CheckCircle, AlertCircle, Minimize2, Maximize2, X } from 'lucide-react';

const DS = {
    accent: '#0AA5BE',
    text: '#e2e2e2',
    textSec: '#888',
    textTer: '#555',
    danger: '#e07a5f',
    success: '#1D9E75',
};

const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
};

export default function UploadProgressPopup({ uploadFiles, onClose, onMinimize, minimized, isDark }) {
    if (!uploadFiles || !uploadFiles.length) return null;

    const listRef = useRef(null);
    const aktivniIndex = uploadFiles.findIndex(f => f.status === 'uploading');

    useEffect(() => {
        if (!listRef.current || minimized) return;
        const container = listRef.current;
        const aktivniEl = container.children[aktivniIndex >= 0 ? aktivniIndex : uploadFiles.length - 1];
        if (aktivniEl) aktivniEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [aktivniIndex, uploadFiles, minimized]);

    const celkem = uploadFiles.length;
    const hotovo = uploadFiles.filter(f => f.status === 'done').length;
    const chyby = uploadFiles.filter(f => f.status === 'error').length;
    const vseDokonceno = hotovo + chyby === celkem;
    const celkovyProgress = celkem > 0
        ? Math.round(uploadFiles.reduce((s, f) => s + (f.progress || 0), 0) / celkem)
        : 0;

    // Minimized floating pill
    if (minimized) {
        return (
            <div
                className="fixed z-[1100] flex items-center gap-3 px-4 py-2.5 cursor-pointer select-none"
                style={{
                    bottom: 24, right: 24,
                    background: isDark ? '#1a1a1a' : '#fff',
                    border: `1px solid ${isDark ? '#2a2a2a' : '#e0e0e0'}`,
                    borderRadius: 9,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                    minWidth: 220,
                }}
                onClick={onMinimize}
            >
                {!vseDokonceno ? (
                    <Loader2 size={16} className="animate-spin" style={{ color: DS.accent }} />
                ) : chyby > 0 ? (
                    <AlertCircle size={16} style={{ color: DS.danger }} />
                ) : (
                    <CheckCircle size={16} style={{ color: DS.success }} />
                )}
                <div className="flex-1">
                    <div className="text-xs font-medium" style={{ color: isDark ? DS.text : '#333' }}>
                        {vseDokonceno
                            ? `Nahráno ${hotovo}/${celkem}${chyby ? ` (${chyby} chyb)` : ''}`
                            : `Nahrávám ${hotovo}/${celkem}…`}
                    </div>
                    {!vseDokonceno && (
                        <div className="mt-1 h-1.5 rounded-full overflow-hidden" style={{ background: isDark ? '#2a2a2a' : '#e0e0e0' }}>
                            <div className="h-full rounded-full transition-all duration-300" style={{ width: `${celkovyProgress}%`, background: DS.accent }} />
                        </div>
                    )}
                </div>
                <Maximize2 size={14} style={{ color: isDark ? DS.textSec : '#888' }} />
            </div>
        );
    }

    // Full popup
    return (
        <div
            className="fixed z-[1100] flex flex-col"
            style={{
                bottom: 24, right: 24,
                width: 380, maxHeight: 420,
                background: isDark ? '#161616' : '#fff',
                border: `1px solid ${isDark ? '#2a2a2a' : '#e0e0e0'}`,
                borderRadius: 10,
                boxShadow: '0 12px 48px rgba(0,0,0,0.35)',
            }}
        >
            {/* Header */}
            <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${isDark ? '#2a2a2a' : '#e8e8e8'}` }}>
                {!vseDokonceno ? (
                    <Loader2 size={16} className="animate-spin" style={{ color: DS.accent }} />
                ) : chyby > 0 ? (
                    <AlertCircle size={16} style={{ color: DS.danger }} />
                ) : (
                    <CheckCircle size={16} style={{ color: DS.success }} />
                )}
                <span className="flex-1 text-sm font-semibold" style={{ color: isDark ? DS.text : '#333' }}>
                    {vseDokonceno
                        ? `Nahrávání dokončeno (${hotovo}/${celkem})`
                        : `Nahrávám soubory… ${hotovo}/${celkem}`}
                </span>
                <button onClick={onMinimize} className="p-1 rounded hover:opacity-70" style={{ color: isDark ? DS.textSec : '#888', background: 'none', border: 'none', cursor: 'pointer' }}>
                    <Minimize2 size={14} />
                </button>
                {vseDokonceno && (
                    <button onClick={onClose} className="p-1 rounded hover:opacity-70" style={{ color: isDark ? DS.textSec : '#888', background: 'none', border: 'none', cursor: 'pointer' }}>
                        <X size={14} />
                    </button>
                )}
            </div>

            {/* Overall progress bar */}
            {!vseDokonceno && (
                <div className="px-4 pt-3 pb-1">
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: isDark ? '#2a2a2a' : '#e0e0e0' }}>
                        <div className="h-full rounded-full transition-all duration-300" style={{ width: `${celkovyProgress}%`, background: DS.accent }} />
                    </div>
                    <div className="text-right mt-1 text-xs" style={{ color: isDark ? DS.textSec : '#888' }}>{celkovyProgress} %</div>
                </div>
            )}

            {/* File list */}
            <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-2" style={{ maxHeight: 280 }}>
                {uploadFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-2.5 py-1.5" style={{ borderBottom: i < celkem - 1 ? `1px solid ${isDark ? '#1e1e1e' : '#f0f0f0'}` : 'none' }}>
                        {f.status === 'done' ? (
                            <CheckCircle size={14} style={{ color: DS.success, flexShrink: 0 }} />
                        ) : f.status === 'error' ? (
                            <AlertCircle size={14} style={{ color: DS.danger, flexShrink: 0 }} />
                        ) : f.status === 'uploading' ? (
                            <Loader2 size={14} className="animate-spin" style={{ color: DS.accent, flexShrink: 0 }} />
                        ) : (
                            <div style={{ width: 14, height: 14, flexShrink: 0 }} />
                        )}
                        <div className="flex-1 min-w-0">
                            <div className="text-xs truncate" style={{ color: isDark ? DS.text : '#333' }}>{f.name}</div>
                            {f.status === 'uploading' && (
                                <div className="mt-1 h-1 rounded-full overflow-hidden" style={{ background: isDark ? '#2a2a2a' : '#e0e0e0' }}>
                                    <div className="h-full rounded-full transition-all duration-150" style={{ width: `${f.progress || 0}%`, background: DS.accent }} />
                                </div>
                            )}
                            {f.status === 'error' && f.error && (
                                <div className="text-xs mt-0.5" style={{ color: DS.danger }}>{f.error}</div>
                            )}
                        </div>
                        <span className="text-xs whitespace-nowrap" style={{ color: isDark ? DS.textTer : '#aaa' }}>
                            {f.status === 'uploading' ? `${f.progress || 0}%` : formatSize(f.size)}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}
